import { useEffect } from 'react';
import PageFormWrapper from '../../../components/layouts/PageFormWrapper';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { useDocumentType } from '../../../hooks/useDocumentType';
import { useAllowedOptions } from '../../../hooks/settings/useAllowedOptions';
import { IDropdownSupplier } from '../../../types/supplier/supplierInterfaces';
import SvgIcon from '../../SvgIcon/SvgIcon';

interface Props {
  title?: string;
  suppliers: IDropdownSupplier[];
  maxWidth?: string;
}

const PurchaseInfoCard = ({
  title = 'Información de la Compra',
  suppliers,
  maxWidth,
}: Props) => {
  const {
    form,
    updateField,
    formErrors,
    handlerUpdatePurchaseHeader,
    purchaseSettlementLoaded,
    correlativeRequested,
    handlerResetPurchaseFormOnly,
  } = useBasePurchase();

  const { documentTypeDropdown, fetchDocumentTypeDropdown } = useDocumentType();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();

  useEffect(() => {
    fetchDocumentTypeDropdown(); // carga todos
    fetchCodesByModuleAndTarget('PURCHASES', 'DOCUMENT_TYPE'); // carga los permitidos
  }, []);

  const allowedDocCodes = getCodes('PURCHASES', 'DOCUMENT_TYPE');

  const isExcludedSubject = form.documentType?.code === '14';

  return (
    <PageFormWrapper title={title} maxWidth={maxWidth}>
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tipo de Documento"
          name="documentType"
          iconKey="list"
          value={form.documentType?.id ?? ''}
          onChange={(e) => {
            const id = Number(e.target.value);
            const selected = documentTypeDropdown.find((d) => d.id === id);
            if (selected) {
              updateField('documentType', {
                id: selected.id,
                name: selected.name,
                code: selected.code,
              });
            }
          }}
          options={documentTypeDropdown
            .filter((doc) => allowedDocCodes.includes(doc.code))
            .map((doc) => ({
              value: doc.id,
              label: doc.name,
            }))}
          error={formErrors.documentType}
          required
          disabled={purchaseSettlementLoaded}
        />

        <InputField
          label="N° Documento"
          name="documentNumber"
          iconKey="hash"
          type="text"
          value={form.documentNumber || ''}
          onChange={(e) => updateField('documentNumber', e.target.value)}
          error={formErrors.documentNumber}
          disabled={
            purchaseSettlementLoaded ||
            correlativeRequested ||
            isExcludedSubject
          }
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DatePicker
          label="Fecha de Compra"
          name="purchaseDate"
          iconKey="calendar"
          value={form.purchaseDate || ''}
          onChange={(date) => updateField('purchaseDate', date)}
          error={formErrors.purchaseDate}
          disabled={purchaseSettlementLoaded || isExcludedSubject}
        />

        <SimpleSearchSelect
          label="Proveedor"
          name="supplier"
          iconKey="listOrdered"
          options={suppliers}
          selectedValue={form.supplier?.id}
          onSelect={(option) => updateField('supplier', option)}
          error={formErrors.supplier}
          disabled={purchaseSettlementLoaded}
        />
      </div>

      {/* Mostrar botón solo si NO está en modo asentamiento */}
      {!purchaseSettlementLoaded && (
        <div className="mt-6 flex justify-end gap-3">
          {/* 🔴 Botón Limpiar */}
          <button
            onClick={handlerResetPurchaseFormOnly}
            className="flex items-center justify-center gap-2 rounded bg-[#DC3545] px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            <SvgIcon name="trash" className="h-5 w-5" />
            Limpiar
          </button>

          {/* 🟢 Botón Actualizar */}
          <button
            onClick={() => handlerUpdatePurchaseHeader(form.id!, form)}
            className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            <SvgIcon name="continue" className="h-5 w-5" />
            Actualizar Compra
          </button>
        </div>
      )}
    </PageFormWrapper>
  );
};

export default PurchaseInfoCard;
